<template>
 <MarketingShell>
 <div class="tm-integrated tm-paymentsuccess">
  <div class="payment-result" :class="{ 'result-dark': isDark }">
    

    <div class="result-container">
      <!-- Loading -->
      <div v-if="verifying" class="result-card">
        <div class="result-spinner"></div>
        <h2>Verifying your payment…</h2>
        <p>Please wait while we confirm your transaction.</p>
      </div>

      <!-- Success -->
      <div v-else-if="success" class="result-card success">
        <div class="result-icon success-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
            stroke="#16a34a" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <h2>Payment Successful</h2>
        <p class="txn-id">Transaction ID: {{ txnId }}</p>
        <p class="plan-info">
          Your <strong>{{ planName }}</strong> subscription is now active.
        </p>
        <button class="btn-go" @click="goToChart">
          Start Charting →
        </button>
      </div>

      <!-- Failure -->
      <div v-else class="result-card failure">
        <div class="result-icon failure-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
            stroke="#dc2626" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>
        <h2>Payment Verification Failed</h2>
        <p>{{ errorMessage }}</p>
        <button class="btn-retry" @click="$router.push('/pricing')">
          Try Again
        </button>
      </div>
    </div>
  </div>

 </div>
 </MarketingShell>
</template>

<script>
import MarketingShell from "./marketing/MarketingShell.vue";
import { verifyPayment } from "../services/payu_service";

export default {
  components: { MarketingShell },
  name: "PaymentSuccess",
  computed: {
    isDark() {
      return this.$store.getters.isDarkTheme;
    }
  },
  data() {
    return {
      verifying: true,
      success: false,
      errorMessage: "We could not verify your payment. If money was deducted, check your account again shortly or contact billing support.",
      txnId: "",
      planName: "",
    };
  },
  async mounted() {
    await this.verify();
  },
  methods: {
    async verify() {
      try {
        const params = this.parseQuery();

        const token = await this.$clerk.getToken();
        if (!token) {
          this.success = false;
          this.errorMessage = "Session expired. Please sign in and check your subscription status.";
          return;
        }

        const result = await verifyPayment(params, token);
        if (result.success) {
          this.success = true;
          this.txnId = result.txnId || params.txnid || "";
          this.planName = result.aiOnly ? "AI" : result.plan === "yearly" ? "Pro Yearly" : "Pro Monthly";
          this.$clerk.setSubscription(result.plan, result.expiresAt);
          this.$store.dispatch("activateSubscription", {
            plan: result.plan,
            expiresAt: result.expiresAt,
            txnId: this.txnId,
          });
        } else {
          this.success = false;
          this.errorMessage = result.error || this.errorMessage;
        }
      } catch (err) {
        console.error("[PaymentSuccess] verify error:", err);
        this.success = false;
        this.errorMessage =
          err.response?.data?.error || err.message || this.errorMessage;
      } finally {
        this.verifying = false;
      }
    },

    goToChart() {
      this.$router.push("/chart");
    },

    parseQuery() {
      const params = {};
      const search = window.location.search.substring(1);
      if (!search) return params;
      search.split("&").forEach((pair) => {
        const [k, v] = pair.split("=");
        params[decodeURIComponent(k)] = decodeURIComponent(v || "");
      });
      return params;
    },
  },
};
</script>

<style scoped>
.payment-result {
  min-height: 100vh;
  background: #fafaf8;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #0f1923;
}

.result-nav {
  display: flex;
  align-items: center;
  padding: 0 48px;
  height: 56px;
  border-bottom: 1px solid #e8e5de;
}
.result-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #0f1923;
}
.result-logo-img {
  height: 30px;
}
.result-logo-text {
  font-family: "DM Serif Display", serif;
  font-size: 18px;
}

.result-container {
  max-width: 480px;
  margin: 80px auto;
  padding: 0 24px;
}

.result-card {
  background: #fff;
  border: 1px solid #e8e5de;
  border-radius: 12px;
  padding: 48px 36px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.04);
}

.result-card h2 {
  font-family: "DM Serif Display", serif;
  font-size: 24px;
  margin: 16px 0 8px;
}

.result-card p {
  font-size: 14px;
  color: #4a5568;
  margin: 0 0 8px;
}

.result-icon {
  margin-bottom: 8px;
}

.txn-id {
  font-family: "DM Mono", monospace;
  font-size: 12px;
  color: #8a9aaa;
}

.plan-info {
  margin-top: 4px;
}

.btn-go {
  margin-top: 24px;
  padding: 12px 32px;
  background: #16a34a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-go:hover {
  background: #15803d;
}

.btn-retry {
  margin-top: 24px;
  padding: 12px 32px;
  background: #0f1923;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-retry:hover {
  background: #16a34a;
}

.result-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e8e5de;
  border-top-color: #16a34a;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin: 0 auto 16px;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Dark theme */
.result-dark {
  background: #0c1018;
  color: #d1d4dc;
}
.result-dark .result-nav { border-bottom-color: rgba(55, 59, 75, 0.5); }
.result-dark .result-logo { color: #d1d4dc; }
.result-dark .result-card {
  background: #121826;
  border-color: rgba(55, 59, 75, 0.5);
  box-shadow: 0 1px 3px rgba(0,0,0,0.2), 0 8px 24px rgba(0,0,0,0.3);
}
.result-dark .result-card h2 { color: #e5e7eb; }
.result-dark .result-card p { color: #9ca3af; }
.result-dark .txn-id { color: #787b86; }
.result-dark .result-spinner { border-color: rgba(55, 59, 75, 0.5); border-top-color: #22c55e; }
.result-dark .btn-retry { background: #e5e7eb; color: #0c1018; }
.result-dark .btn-retry:hover { background: #22c55e; color: #fff; }
</style>
